import type { I18n } from "@lingui/core";
import { DateTime } from "luxon";
import { formatDate } from "../formatDate";
import { formatDuration } from "../formatDuration";
import { formatList } from "../formatList";

type RuleMessageContext = Record<string, unknown>;

type FormatRuleMessageValuesParams = {
	context?: RuleMessageContext;
	i18n: I18n;
	locale: string;
	timezone?: string | null;
};

const isStringList = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every((item) => typeof item === "string");

const formatDateValue = ({
	value,
	formatType,
	locale,
	timezone,
}: {
	value: string;
	formatType: "Abbreviated_DateTime" | "Abbreviated_Date" | "Time";
	locale: string;
	timezone?: string | null;
}) => {
	if (formatType === "Time") {
		const time = DateTime.fromFormat(value, value.split(":").length === 3 ? "H:mm:ss" : "H:mm", { zone: "utc" });
		return time.isValid
			? formatDate({ date: time.toJSDate(), formatType, timezone: "UTC", language: locale || "en" })
			: value;
	}
	const configuredFormat = formatType === "Abbreviated_Date" ? "yyyy-MM-dd" : "yyyy-MM-dd H:mm:ss";
	const configured = DateTime.fromFormat(value, configuredFormat, { zone: "utc" });
	if (configured.isValid) {
		return formatDate({ date: configured.toJSDate(), formatType, timezone: "UTC", language: locale || "en" });
	}
	return formatDate({
		date: value,
		formatType,
		timezone,
		language: locale || "en",
	});
};

export const formatRuleMessageValues = ({ context, i18n, locale, timezone }: FormatRuleMessageValuesParams) => {
	if (!context) {
		return undefined;
	}

	return Object.fromEntries(
		Object.entries(context).map(([key, value]) => {
			if (isStringList(value)) {
				return [key, formatList({ values: value, locale })];
			}
			if ((key === "datetime" || key === "rangeStartISO" || key === "rangeEndISO") && typeof value === "string") {
				return [key, formatDateValue({ value, formatType: "Abbreviated_DateTime", locale, timezone })];
			}
			if (key === "date" && typeof value === "string") {
				return [key, formatDateValue({ value, formatType: "Abbreviated_Date", locale, timezone })];
			}
			if (key === "time" && typeof value === "string") {
				return [key, formatDateValue({ value, formatType: "Time", locale, timezone })];
			}
			if (key === "duration" && typeof value === "number") {
				return [key, formatDuration({ duration: value, i18n })];
			}
			return [key, value];
		}),
	);
};
